import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { managerDashboardAPI } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { getInitials, formatTime } from '../../lib/utils';
import {
  Users,
  UserCheck,
  UserX,
  Calendar,
  Clock,
  AlertTriangle
} from 'lucide-react';

const ManagerDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!user?.company_id) return;
    try {
      const [dashRes, teamRes] = await Promise.all([
        managerDashboardAPI.getTeamDashboard(),
        managerDashboardAPI.getTeam()
      ]);
      setData(dashRes.data);
      setTeam(teamRes.data);
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [user?.company_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <DashboardLayout title="Team Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Calculate team stats
  const teamSize = team.length;
  const presentCount = data?.present_count || 0;
  const absentCount = data?.absent_count || 0;
  const lateCount = data?.late_count || 0;
  const onLeaveCount = data?.on_leave_count || 0;
  const otToday = data?.ot_hours_today || 0;
  const attendanceRate = teamSize > 0 ? Math.round((presentCount / teamSize) * 100) : 0;

  return (
    <DashboardLayout 
      title="Team Dashboard" 
      subtitle="Monitor your team&apos;s attendance and performance"
    >
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <div>
                <p className="text-2xl font-bold">{teamSize}</p>
                <p className="text-xs text-muted-foreground">Team Size</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-emerald-500" />
              <div>
                <p className="text-2xl font-bold">{presentCount}</p>
                <p className="text-xs text-muted-foreground">Present</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <UserX className="h-5 w-5 text-red-500" />
              <div>
                <p className="text-2xl font-bold">{absentCount}</p>
                <p className="text-xs text-muted-foreground">Absent</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <div>
                <p className="text-2xl font-bold">{lateCount}</p>
                <p className="text-xs text-muted-foreground">Late</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{onLeaveCount}</p>
                <p className="text-xs text-muted-foreground">On Leave</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{otToday}h</p>
                <p className="text-xs text-muted-foreground">OT Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Attendance Rate + Pending */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader>
            <CardTitle>Team Attendance Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-4">
              <p className="text-5xl font-bold text-primary mb-2">{attendanceRate}%</p>
              <Progress value={attendanceRate} className="h-3 mb-4" />
              <p className="text-sm text-muted-foreground">
                {presentCount} of {teamSize} team members present
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pending Approvals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-500" />
                  <span>Leave Requests</span>
                </div>
                <Badge variant={data?.pending_leaves > 0 ? "destructive" : "secondary"}>
                  {data?.pending_leaves || 0}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-purple-500" />
                  <span>Shift Changes</span>
                </div>
                <Badge variant={data?.pending_shift_changes > 0 ? "destructive" : "secondary"}>
                  {data?.pending_shift_changes || 0}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Present Employees */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-emerald-500" />
            Present Team Members
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data?.present_employees?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.present_employees.map((emp) => (
                <div key={emp.id} className="flex items-center gap-3 p-3 rounded-lg border border-border">
                  <Avatar>
                    <AvatarImage src={emp.avatar_url} />
                    <AvatarFallback>{getInitials(emp.first_name, emp.last_name)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{emp.first_name} {emp.last_name}</p>
                    <p className="text-xs text-muted-foreground">
                      In: {formatTime(emp.attendance?.clock_in)}
                    </p>
                  </div>
                  <Badge className="status-present">Present</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No team members present yet</p>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default ManagerDashboard;
